import { createContext, useContext, type ReactNode } from "react";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import {
  usePresence,
  getPresenceStatus,
  type PresenceStatus,
} from "@/hooks/use-presence";

interface PresenceContextValue {
  presence: Map<string, PresenceStatus>;
}

const PresenceContext = createContext<PresenceContextValue>({
  presence: new Map(),
});

/**
 * Oppretter én delt presence-subscription for hele appen og eksponerer
 * resultatet via context. Plasseres høyt i treet (AppLayout) slik at
 * alle chat-komponenter kan lese status uten egne subscriptions.
 */
export function PresenceProvider({ children }: { children: ReactNode }) {
  const { activeUser } = useImpersonation();
  const presence = usePresence(activeUser?.id);
  return (
    <PresenceContext.Provider value={{ presence }}>
      {children}
    </PresenceContext.Provider>
  );
}

/** Les hele presence-mappet. */
export function usePresenceMap() {
  return useContext(PresenceContext).presence;
}

/** Les status for én bruker. Standard: "offline". */
export function usePresenceStatus(
  userId: string | null | undefined,
): PresenceStatus {
  const { presence } = useContext(PresenceContext);
  return getPresenceStatus(presence, userId);
}
